/**
 * Route Parameter Validation Utility
 * Prevents open redirect attacks by validating route parameters.
 *
 * Security model for validatePathname:
 *   - Applies Unicode NFC normalization before any checks (collapses lookalike code points)
 *   - Rejects null bytes and ASCII control characters (0x00–0x1F, 0x7F)
 *   - Rejects ALL percent-encoded characters ('%') — blocks %3A (colon), %2F (slash),
 *     %2e%2e (dot-dot) and any other encoded-scheme or encoded-traversal bypass
 *   - Rejects backslashes (Windows UNC path and browser normalisation bypass)
 *   - Rejects protocol-relative URLs (//host)
 *   - Rejects absolute URLs with any URI scheme (https:, javascript:, data:, ftp:, …)
 *   - Must start with '/' (relative paths only)
 *   - String-level '..' guard on the path portion before per-segment validation
 *   - Path segments: alphanumeric, hyphens, underscores only — no colons, no dots
 *   - Query/hash suffix: conservative printable ASCII subset — no '/', no '%', no '<>'
 *   - Maximum 2048 characters total
 */

/**
 * Validates a single route parameter.
 * Allows Unicode letters, Unicode digits, spaces, hyphens, and underscores.
 * This covers internationalized asset names (e.g. CJK characters) as well as
 * ASCII-only names, while still blocking control characters, slashes, URL
 * delimiters, and traversal-like or scheme-like constructs.
 *
 * Also preserves common safe punctuation used in short names, such as:
 * ampersand (&), apostrophe ('), dot (.), comma (,), parentheses, and plus (+).
 *
 * Explicitly excluded: '/', '\\', '?', '#', ':', ';', '@', '[', ']', '{', '}',
 * '<', '>', '"', '`', '|', '^', and '='.
 *
 * Colons are excluded to prevent scheme injection (javascript:, data:, etc.).
 * Dots are allowed but standalone '.' and '..' are rejected.
 * Properly handles percent-encoded input (e.g., %20 for space, %E8%B5%84 for
 * Chinese characters) by decoding and validating the decoded form, supporting
 * internationalized asset names in URLs while preventing encoding exploits.
 * Limits to 256 characters.
 */
export const validateRouteParam = (param: string | undefined): string => {
  if (!param || typeof param !== 'string') {
    return '';
  }
  // NFC normalize before validation to collapse Unicode lookalike code points
  let sanitized = param.normalize('NFC').trim().substring(0, 256);
  // Reject control characters and null bytes outright
  if (/[\x00-\x1F\x7F]/.test(sanitized)) {
    return '';
  }

  // Handle percent-encoded input (e.g., %20 for space, %E8%B5%84 for Chinese).
  // This is necessary because when URLs with non-ASCII characters or spaces are
  // processed by the browser, window.location.pathname contains the percent-encoded
  // form. We decode it first, then validate the decoded value to prevent double-
  // encoding attacks while still supporting internationalized asset names.
  if (sanitized.includes('%')) {
    // Reject malformed encoding (% not followed by exactly 2 hex digits)
    if (/%(?![0-9A-Fa-f]{2})/.test(sanitized)) {
      return '';
    }
    try {
      // Decode the percent-encoded string
      sanitized = decodeURIComponent(sanitized);
      // Re-normalize after decoding to ensure consistency
      sanitized = sanitized.normalize('NFC').substring(0, 256);
      // Re-check for control characters after decoding
      if (/[\x00-\x1F\x7F]/.test(sanitized)) {
        return '';
      }
    } catch {
      return ''; // Decoding failed (invalid encoding)
    }
  }

  // Reject standalone traversal-like segments.
  if (sanitized === '.' || sanitized === '..') {
    return '';
  }

  // Remove dangerous URL delimiters and path separators, but preserve commonly used
  // punctuation in short names (e.g., '&', '.', ',', '(', ')', '+', apostrophe).
  sanitized = sanitized.replace(/[\/:;?#@\[\]{}<>"`|^=\\]/g, '');
  // Reject if this introduced an empty value.
  if (!sanitized.trim()) {
    return '';
  }
  // Reject traversal sequence if it still appears after stripping delimiters.
  if (sanitized.includes('..')) {
    return '';
  }

  // Finally, strip control/format characters while retaining printable Unicode text.
  sanitized = sanitized.replace(/[\p{C}]/gu, '');
  return sanitized;
};

/**
 * Validates a pathname/redirect path against open redirect and path-traversal attacks.
 *
 * Blocks:
 *   - Non-string / empty values
 *   - Paths exceeding 2048 characters
 *   - Null bytes and ASCII control characters (bypass in some parsers)
 *   - Any percent-encoded character ('%') — prevents %2e%2e (..), %3A (scheme colon),
 *     %2F (slash), %6A%61%76%61%73%63%72%69%70%74 (javascript), and all variants
 *   - Backslashes (Windows UNC path and browser normalisation bypasses like /foo\..\bar)
 *   - Protocol-relative URLs (//example.com)
 *   - Absolute URLs with any URI scheme (https:, javascript:, data:, ftp:, etc.)
 *   - Paths that do not start with '/'
 *   - Path portions containing '..' (string-level guard before per-segment check)
 *   - Path segments containing characters other than [a-zA-Z0-9\-_]
 *     (colons excluded — URI scheme formation; dots excluded — traversal)
 *   - Query/hash suffixes outside a conservative printable ASCII allowlist
 */
export const validatePathname = (pathname: string | undefined): string => {
  if (!pathname || typeof pathname !== 'string') {
    return '';
  }

  // Unicode NFC normalization collapses equivalent code points (e.g. decomposed
  // Unicode lookalikes) so that subsequent character checks are reliable.
  const trimmed = pathname.normalize('NFC').trim();

  // Enforce max length before any further processing
  if (trimmed.length > 2048) {
    return '';
  }

  // Reject null bytes and ASCII control characters (0x00–0x1F, 0x7F).
  // These can be used to truncate strings or bypass filters in some parsers.
  if (/[\x00-\x1F\x7F]/.test(trimmed)) {
    return '';
  }

  // Handle percent-encoded paths (e.g. paths produced by React Router's generatePath
  // when assetName or other params contain Unicode / space characters).
  // Strategy: decode safely, then run the decoded form through the same validation.
  // This prevents encoded-scheme bypasses (%3A%2F%2F → ://, %6A…→ javascript:)
  // while allowing legitimate internationalized paths (%E8%B5%84 → 资产).
  if (trimmed.includes('%')) {
    // Reject lone '%' or '%X' (malformed / incomplete percent encoding)
    if (/%(?![0-9A-Fa-f]{2})/.test(trimmed)) {
      return '';
    }
    let decoded: string;
    try {
      decoded = decodeURIComponent(trimmed);
    } catch {
      return ''; // malformed encoding — reject
    }
    // Re-validate the decoded form through this same function.
    // All structural checks (scheme, traversal, segment allowlist) are enforced on
    // the decoded string.  If safe, return the original percent-encoded path so the
    // browser / React Router receives a properly encoded URL.
    return validatePathname(decoded) !== '' ? trimmed : '';
  }

  // Reject backslashes: browsers normalize \\ and /\ to / in some contexts,
  // enabling traversal (e.g. /foo\..\bar) and UNC paths (\\server\share).
  if (trimmed.includes('\\')) {
    return '';
  }

  // Reject protocol-relative URLs (//example.com)
  if (trimmed.startsWith('//')) {
    return '';
  }

  // Reject any URI scheme at the start (e.g. https:, javascript:, data:, ftp:).
  // Covers lowercase, uppercase, and mixed-case variants.
  if (/^[a-zA-Z][a-zA-Z0-9+.-]*:/i.test(trimmed)) {
    return '';
  }

  // Must be a relative path (must start with '/')
  if (!trimmed.startsWith('/')) {
    return '';
  }

  // Split into path portion and query/hash suffix to validate independently
  const queryIndex = trimmed.search(/[?#]/);
  const pathPart = queryIndex === -1 ? trimmed : trimmed.slice(0, queryIndex);
  const querySuffix = queryIndex === -1 ? '' : trimmed.slice(queryIndex);

  // String-level traversal guard: reject if '..' appears anywhere in the path portion.
  // This is belt-and-suspenders alongside the per-segment check below and makes the
  // intent explicit for static analysis tools.
  if (pathPart.includes('..')) {
    return '';
  }

  // Per-segment validation after splitting on '/'.
  // Each non-empty segment must match the Unicode-aware allowlist:
  //   \p{L}  — Unicode letters (covers ASCII a-z, A-Z and CJK / Latin-extended etc.)
  //   \p{N}  — Unicode digits  (covers ASCII 0-9 and other script numerals)
  //   space  — allowed (produced by decoding %20 in internationalized asset names)
  //   hyphen / underscore — allowed
  //
  // Explicitly excluded:
  //   colons  → prevents URI scheme formation (/javascript:alert)
  //   dots    → prevents traversal ('..' and '.')
  //   slashes → already split on '/'; can't appear inside a segment
  //   percent → already handled above (decoded branch) or absent
  const segments = pathPart.split('/');
  for (const segment of segments) {
    // Explicit traversal check (mirrors the string-level guard; static-analysis friendly)
    if (segment === '..' || segment === '.') {
      return '';
    }
    if (segment !== '' && !/^[\p{L}\p{N}\-_ ]+$/u.test(segment)) {
      return '';
    }
  }

  // Validate query string / hash suffix against a conservative printable ASCII allowlist.
  // Excluded: '%' (already blocked), '/' (not needed in query values — reduces attack
  // surface), '<' '>' (XSS vectors), control characters.
  if (querySuffix && !/^[?#][a-zA-Z0-9\-_=&.#+@!~*'(),]*$/.test(querySuffix)) {
    return '';
  }

  return trimmed;
};

/**
 * Validates multiple route parameters (like from useParams())
 * Returns object with sanitized values
 */
export const validateRouteParams = (params: Record<string, any>): Record<string, string> => {
  const validated: Record<string, string> = {};

  for (const [key, value] of Object.entries(params)) {
    validated[key] = validateRouteParam(value);
  }

  return validated;
};

/**
 * Constructs a safe URL from a trusted route template and validated parameters.
 *
 * @param basePath - A static route template string (e.g. '/workflow/:id').
 *   Must start with '/' and contain only alphanumeric, hyphens, underscores,
 *   slashes, and ':paramName' placeholders. MUST NOT be user-controlled input.
 * @param params   - User-supplied parameter values; each is sanitized via
 *   validateRouteParam before substitution.
 *
 * Returns '' if basePath fails its template pattern check, if a required
 * placeholder is left unresolved (contains ':' in the output), or if params
 * produce an empty value for a named segment.
 */
export const constructSafeUrl = (basePath: string, params: Record<string, any> = {}): string => {
  if (!basePath || typeof basePath !== 'string') {
    return '';
  }

  // Validate basePath as a route template.  Segments may be literal path
  // components (alphanumeric / hyphen / underscore) or ':paramName' placeholders.
  // This ensures basePath itself was never sourced from user-controlled data.
  if (!basePath.startsWith('/') || !/^[a-zA-Z0-9\-_/:]+$/.test(basePath)) {
    return '';
  }

  let url = basePath;
  const validatedParams = validateRouteParams(params);

  for (const [key, value] of Object.entries(validatedParams)) {
    if (value) {
      url = url.replace(`:${key}`, value);
    }
  }

  // Reject the result if any ':param' placeholder remains unresolved.
  // A colon in the output would allow scheme injection (e.g. javascript:).
  if (url.includes(':')) {
    return '';
  }

  return url;
};
